<template>
	<div class="relative z-20">
		<header class="h-16 font-semibold text-white bg-neutral-900">
			<Container
				:wide="isCompanyDashboard"
				class="flex items-center justify-between h-full"
			>
				<div class="flex items-center py-4 mr-auto">
					<!-- Apropos logo -->
					<NuxtLink :to="logoDestination">
						<Logo class="block h-auto w-9" />
					</NuxtLink>

					<div v-if="$user" class="hidden ml-12 gap-x-8 lg:flex">
						<!-- Dashboard / Applications / Talent Pool / Profile -->
						<NuxtLink
							v-for="link in desktopPrimaryLinks"
							:key="link.key"
							:to="link.to"
							class="flex items-center font-normal gap-x-1.5 text-white hover:bg-[#1F1F1F] py-2 px-3 rounded-lg"
							active-class="bg-[#2A2C2C] !font-bold"
						>
							<Component
								:is="link.iconComponent"
								class="shrink-0 pb-0.5"
								size="20"
							/>
							<span
								class="header-nav-label"
								:data-text="$t(link.labelKey)"
							>
								{{ $t(link.labelKey) }}
							</span>
						</NuxtLink>
					</div>
				</div>

				<div class="flex items-center">
					<div v-if="$user" class="flex items-center gap-x-6">
						<!-- Notifications -->
						<NotificationsBellButton
							v-if="showNotificationsBell"
							:count="notificationCount"
							@click.stop="
								$store.dispatch('notification/toggleSlideout')
							"
						/>

						<!-- Language selector -->
						<LanguageSwitcher align="bottom-end" />

						<!-- Organization dropdown menu -->
						<OrganizationDropdown v-if="$org" />

						<!-- User dropdown menu -->
						<ActionMenu
							@dropdownIsOpen="profileDropdown"
							align="bottom-end"
						>
							<template #toggle>
								<button
									type="button"
									class="flex items-center gap-2 py-1.5 text-white"
								>
									<template v-if="$user.avatar">
										<div
											:style="avatarStyle"
											class="size-6 rounded-full bg-[#e3e3e3] bg-cover bg-center ring-1 ring-white/20"
										/>
										<IconChevronDown
											class="transition-transform"
											:class="
												profileDropdownOpen
													? 'rotate-180'
													: ''
											"
										/>
									</template>

									<div
										v-else
										class="flex items-center gap-2 cursor-pointer"
									>
										<IconUserProfile />
										<IconChevronDown
											class="transition-transform"
											:class="
												profileDropdownOpen
													? 'rotate-180'
													: ''
											"
										/>
									</div>
								</button>
							</template>

							<!-- Dropdown heading -->
							<div class="px-4 py-2 font-bold text-neutral-900">
								{{ $user.fullName }}
							</div>

							<!-- Profile -->
							<ActionMenuItem
								to="/user/profile"
								icon="user-circle"
							>
								{{ $t('user.profile') }}
							</ActionMenuItem>

							<!-- Leave impersonation -->
							<ActionMenuItem
								v-if="isImpersonating"
								icon="user-clock"
								@click="leaveImpersonation"
							>
								{{ $t('auth.leave-impersonation') }}
							</ActionMenuItem>

							<!-- Log out -->
							<ActionMenuItem to="/auth/logout" icon="sign-in">
								{{ $t('auth.sign-out') }}
							</ActionMenuItem>
						</ActionMenu>

						<!-- Mobile menu dropdown -->
						<div class="lg:hidden">
							<Button
								@click.stop="$store.commit('toggleSidebar')"
							>
								<Icon
									class="text-white"
									:icon="[
										'far',
										$store.state.sidebarOpen
											? 'times'
											: 'bars'
									]"
									size="lg"
								/>
							</Button>
						</div>
					</div>
					<div v-else>
						<NuxtLink
							class="text-white transition-colors hover:text-[#c1c1c1]"
							to="/auth/login"
						>
							{{ $t('auth.log-in') }}
						</NuxtLink>
					</div>
				</div>
			</Container>
		</header>

		<div class="h-[5px] w-full bg-brand-rainbow" />
	</div>
</template>

<script>
import Button from '~/components/ui/common/Button'
import Icon from '~/components/ui/common/Icon'
import Container from '~/components/ui/layout/Container'
import LanguageSwitcher from '~/components/language-switcher'
import {
	IconFile,
	IconHome,
	IconUser,
	IconUsers,
	IconUserProfile,
	IconChevronDown
} from '~/components/Icons'
import { Logo } from '~/components/Logo'
import {
	ActionMenu,
	ActionMenuItem,
	NotificationsBellButton,
	OrganizationDropdown
} from './_internals'
import { buildHeaderNavigation } from './navigation'

function hasImpersonationData() {
	return Boolean(
		typeof window !== 'undefined' &&
			window.localStorage.getItem('impersonation_data')
	)
}

export default {
	name: 'AppHeader',
	components: {
		OrganizationDropdown,
		ActionMenu,
		ActionMenuItem,
		Button,
		NotificationsBellButton,
		Icon,
		Container,
		LanguageSwitcher,
		IconFile,
		IconHome,
		IconUser,
		IconUsers,
		IconUserProfile,
		IconChevronDown,
		Logo
	},
	data() {
		return {
			profileDropdownOpen: false,
			isImpersonating: hasImpersonationData()
		}
	},
	computed: {
		isCompanyDashboard() {
			return this.$route?.path === '/dashboard' && Boolean(this.$org)
		},
		navigation() {
			return buildHeaderNavigation({
				hasOrg: Boolean(this.$org),
				hasApplicantProfile: Boolean(this.$user?.applicant_profile),
				hasActiveApplicantProfile: Boolean(
					this.$applicantProfile && this.$applicantProfile.active
				)
			})
		},
		logoDestination() {
			return this.$user ? '/dashboard' : '/'
		},
		desktopPrimaryLinks() {
			return this.navigation.primary
				.filter(link => link.showOnDesktop)
				.sort((left, right) => (left.order || 99) - (right.order || 99))
		},
		avatarStyle() {
			if (!this.$user?.avatar) {
				return null
			}

			return {
				backgroundImage: `url(${this.$user.avatar.url})`,
				backgroundClip: 'padding-box',
				width: '1.5rem',
				height: '1.5rem'
			}
		},
		showNotificationsBell() {
			return Boolean(this.$user)
		},
		notificationCount() {
			return this.$store.state.notification.unreadCount || 0
		}
	},
	methods: {
		syncImpersonationState() {
			this.isImpersonating = hasImpersonationData()
		},
		async leaveImpersonation() {
			try {
				await this.$store.dispatch('user/leaveImpersonation')
				this.isImpersonating = false
				this.$store.dispatch('toast/push', {
					title: this.$t('auth.left-impersonation-title'),
					message: this.$t('auth.left-impersonation-message'),
					type: 'success'
				})
			} catch (error) {
				if (
					error?.impersonationEnded &&
					!error?.impersonationIdentityCleared
				) {
					this.isImpersonating = false
					this.$store.dispatch('toast/push', {
						title: this.$t('auth.left-impersonation-title'),
						message: this.$t('auth.left-impersonation-message'),
						type: 'success'
					})
					return
				}

				if (error?.impersonationIdentityCleared) {
					this.isImpersonating = false
					return
				}

				this.syncImpersonationState()
				this.$store.dispatch('toast/push', {
					title: this.$t('auth.leave-impersonation-failed-title'),
					message: this.$t('auth.leave-impersonation-failed-message'),
					type: 'danger'
				})
			}
		},
		profileDropdown(value) {
			this.profileDropdownOpen = value
		}
	},
	mounted() {
		this.syncImpersonationState()
	}
}
</script>

<style>
.header-nav-label {
	display: inline-grid;
}

.header-nav-label::after {
	content: attr(data-text);
	grid-area: 1 / 1;
	height: 0;
	overflow: hidden;
	visibility: hidden;
	font-weight: 700;
}
</style>
